const PANE_DROP_CLASS = 'files-browse-files--drop-target';
const ROW_DROP_CLASS = 'files-dir-tree__row--drop-target';
const TREE_DROP_CLASS = 'files-dir-tree--drop-target';

export interface BindFilesDropUploadOptions {
  root: HTMLElement;
  getCurrentPath(): string;
  canUpload(): boolean;
  isBusy(): boolean;
  onDrop(files: File[], targetPath: string): void;
  onRejectedEmpty?(files: File[]): void;
}

export function partitionDroppedFiles(files: File[]): {
  accepted: File[];
  rejectedEmpty: File[];
} {
  const accepted: File[] = [];
  const rejectedEmpty: File[] = [];
  for (const file of files) {
    if (file.size === 0) {
      rejectedEmpty.push(file);
    } else {
      accepted.push(file);
    }
  }
  return { accepted, rejectedEmpty };
}

function eventElement(event: Event): Element | null {
  return event.target instanceof Element ? event.target : null;
}

export function isExternalFileDrag(event: DragEvent): boolean {
  const types = event.dataTransfer?.types;
  if (!types) {
    return false;
  }
  return Array.from(types).includes('Files');
}

export function filesFromDataTransfer(dataTransfer: DataTransfer): File[] {
  const files: File[] = [];
  const items = dataTransfer.items;
  if (items && items.length > 0) {
    for (let index = 0; index < items.length; index++) {
      const item = items[index];
      if (item.kind !== 'file') {
        continue;
      }
      const entry = item.webkitGetAsEntry?.() ?? null;
      if (entry?.isDirectory) {
        continue;
      }
      const file = item.getAsFile();
      if (file) {
        files.push(file);
      }
    }
    return files;
  }

  return Array.from(dataTransfer.files);
}

export function resolveFilesDropPath(event: Event, currentPath: string): string | null {
  const el = eventElement(event);
  if (!el) {
    return null;
  }
  if (el.closest('.files-dir-tree')) {
    const pathEl = el.closest('[data-tree-path]');
    if (pathEl instanceof HTMLElement) {
      return pathEl.dataset.treePath ?? '';
    }
    return currentPath;
  }
  if (el.closest('.files-browse-files')) {
    return currentPath;
  }
  return null;
}

function clearDropHighlights(root: HTMLElement): void {
  root.classList.remove(PANE_DROP_CLASS, TREE_DROP_CLASS);
  for (const el of root.querySelectorAll(`.${PANE_DROP_CLASS}, .${ROW_DROP_CLASS}, .${TREE_DROP_CLASS}`)) {
    el.classList.remove(PANE_DROP_CLASS, ROW_DROP_CLASS, TREE_DROP_CLASS);
  }
}

function applyDropHighlight(root: HTMLElement, event: Event, allowed: boolean, path: string | null): void {
  clearDropHighlights(root);
  if (!allowed || path === null) {
    return;
  }

  const el = eventElement(event);
  if (!el) {
    return;
  }

  const row = el.closest('.files-dir-tree__row');
  if (row) {
    row.classList.add(ROW_DROP_CLASS);
    return;
  }

  const pane = el.closest('.files-browse-files');
  if (pane) {
    pane.classList.add(PANE_DROP_CLASS);
    return;
  }

  const tree = el.closest('.files-dir-tree');
  if (tree) {
    tree.classList.add(TREE_DROP_CLASS);
  }
}

export function bindSimpleFileDropzone(options: {
  root: HTMLElement;
  canAccept(): boolean;
  isBusy(): boolean;
  onFiles(files: File[]): void;
  onRejectedEmpty?(files: File[]): void;
  dropClass?: string;
}): () => void {
  const dropClass = options.dropClass ?? 'info-share-files--drop-target';

  const clearHighlight = () => {
    options.root.classList.remove(dropClass);
  };

  const onDragOver = (event: DragEvent) => {
    if (!isExternalFileDrag(event)) {
      return;
    }
    event.preventDefault();
    const allowed = options.canAccept() && !options.isBusy();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = allowed ? 'copy' : 'none';
    }
    if (allowed) {
      options.root.classList.add(dropClass);
    } else {
      clearHighlight();
    }
  };

  const onDragLeave = (event: DragEvent) => {
    if (!isExternalFileDrag(event)) {
      return;
    }
    const next = event.relatedTarget;
    if (next instanceof Node && options.root.contains(next)) {
      return;
    }
    clearHighlight();
  };

  const onDrop = (event: DragEvent) => {
    if (!isExternalFileDrag(event)) {
      return;
    }
    event.preventDefault();
    clearHighlight();
    if (!options.canAccept() || options.isBusy() || !event.dataTransfer) {
      return;
    }
    const files = filesFromDataTransfer(event.dataTransfer);
    if (files.length === 0) {
      return;
    }
    const { accepted, rejectedEmpty } = partitionDroppedFiles(files);
    if (rejectedEmpty.length > 0) {
      options.onRejectedEmpty?.(rejectedEmpty);
    }
    if (accepted.length === 0) {
      return;
    }
    options.onFiles(accepted);
  };

  options.root.addEventListener('dragenter', onDragOver);
  options.root.addEventListener('dragover', onDragOver);
  options.root.addEventListener('dragleave', onDragLeave);
  options.root.addEventListener('drop', onDrop);

  return () => {
    options.root.removeEventListener('dragenter', onDragOver);
    options.root.removeEventListener('dragover', onDragOver);
    options.root.removeEventListener('dragleave', onDragLeave);
    options.root.removeEventListener('drop', onDrop);
    clearHighlight();
  };
}

export function bindFilesDropUpload(options: BindFilesDropUploadOptions): () => void {
  const { root } = options;

  const onDragOver = (event: DragEvent) => {
    if (!isExternalFileDrag(event)) {
      return;
    }
    event.preventDefault();
    const path = resolveFilesDropPath(event, options.getCurrentPath());
    const allowed = options.canUpload() && !options.isBusy() && path !== null;
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = allowed ? 'copy' : 'none';
    }
    applyDropHighlight(root, event, allowed, path);
  };

  const onDragLeave = (event: DragEvent) => {
    if (!isExternalFileDrag(event)) {
      return;
    }
    const next = event.relatedTarget;
    if (next instanceof Node && root.contains(next)) {
      return;
    }
    clearDropHighlights(root);
  };

  const onDrop = (event: DragEvent) => {
    if (!isExternalFileDrag(event)) {
      return;
    }
    event.preventDefault();
    clearDropHighlights(root);
    if (!options.canUpload() || options.isBusy() || !event.dataTransfer) {
      return;
    }
    const path = resolveFilesDropPath(event, options.getCurrentPath());
    if (path === null) {
      return;
    }
    const files = filesFromDataTransfer(event.dataTransfer);
    if (files.length === 0) {
      return;
    }
    const { accepted, rejectedEmpty } = partitionDroppedFiles(files);
    if (rejectedEmpty.length > 0) {
      options.onRejectedEmpty?.(rejectedEmpty);
    }
    if (accepted.length === 0) {
      return;
    }
    options.onDrop(accepted, path);
  };

  root.addEventListener('dragenter', onDragOver);
  root.addEventListener('dragover', onDragOver);
  root.addEventListener('dragleave', onDragLeave);
  root.addEventListener('drop', onDrop);

  return () => {
    root.removeEventListener('dragenter', onDragOver);
    root.removeEventListener('dragover', onDragOver);
    root.removeEventListener('dragleave', onDragLeave);
    root.removeEventListener('drop', onDrop);
    clearDropHighlights(root);
  };
}
